import React, { useState, useEffect } from 'react';
// import { useAuthStore } from '@/app/store/authStore';
import { 
  PaginationState, 
  SortingState, 
  ColumnFiltersState, 
} from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

// Import services
import { 
  listVoucherTypes, 
  createVoucherType, 
  updateVoucherType, 
  deleteVoucherType 
} from '@/app/services/voucherService';

// Import types
import { VoucherTypeResponse, VoucherTypeCreate, VoucherTypeUpdate } from '@/app/types';

// Import components
import { columnsVoucherType } from './components/columnsVoucherType';
import VoucherTypeDataTable from './components/VoucherTypeDataTable';
import VoucherTypeForm from './components/VoucherTypeForm';

// Default pagination state
const defaultPagination: PaginationState = {
  pageIndex: 0,
  pageSize: 10,
};

const VoucherTypesPage: React.FC = () => {
  const queryClient = useQueryClient();

  // State
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<ColumnFiltersState>([]);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingVoucherType, setEditingVoucherType] = useState<VoucherTypeResponse | null>(null);
  const [deletingVoucherType, setDeletingVoucherType] = useState<VoucherTypeResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Event listeners for UI events
  useEffect(() => {
    const handleEditVoucherType = (e: CustomEvent<VoucherTypeResponse>): void => {
      setEditingVoucherType(e.detail);
      setFormOpen(true);
    };

    const handleDeleteVoucherType = (e: CustomEvent<VoucherTypeResponse>): void => {
      setDeletingVoucherType(e.detail);
      setDeleteDialogOpen(true);
    };

    // Register event listeners
    document.addEventListener('edit-voucher-type', handleEditVoucherType as EventListener);
    document.addEventListener('delete-voucher-type', handleDeleteVoucherType as EventListener);

    // Cleanup
    return () => {
      document.removeEventListener('edit-voucher-type', handleEditVoucherType as EventListener);
      document.removeEventListener('delete-voucher-type', handleDeleteVoucherType as EventListener);
    };
  }, []);

  // API Queries
  const {
    data: voucherTypesData = { items: [], total: 0 },
    isLoading: isLoadingVoucherTypes,
    error: voucherTypesError,
  } = useQuery({
    queryKey: [
      'voucherTypes',
      {
        pagination,
        sorting,
        filters,
        search,
      },
    ],
    queryFn: () => listVoucherTypes({
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      sort_by: sorting.length > 0 ? sorting[0].id : undefined,
      sort_dir: sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
      search_query: search || undefined,
    }),
  });

  // Mutations
  const createVoucherTypeMutation = useMutation({
    mutationFn: (newVoucherType: VoucherTypeCreate) => createVoucherType(newVoucherType),
    onSuccess: () => {
      toast.success('Voucher type created successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherTypes'] });
      setFormOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to create voucher type');
      console.error('Error creating voucher type:', error);
    },
  });

  const updateVoucherTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherTypeUpdate }) => 
      updateVoucherType(id, data),
    onSuccess: () => {
      toast.success('Voucher type updated successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherTypes'] });
      setFormOpen(false);
      setEditingVoucherType(null);
    },
    onError: (error) => {
      toast.error('Failed to update voucher type');
      console.error('Error updating voucher type:', error);
    },
  });

  const deleteVoucherTypeMutation = useMutation({
    mutationFn: (id: number) => deleteVoucherType(id),
    onSuccess: () => {
      toast.success('Voucher type deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherTypes'] });
      setDeleteDialogOpen(false);
      setDeletingVoucherType(null);
    },
    onError: (error) => {
      toast.error('Failed to delete voucher type. It may be in use by vouchers.');
      console.error('Error deleting voucher type:', error);
    },
  });

  // Handle form submission
  const handleVoucherTypeSubmit = (data: VoucherTypeCreate | VoucherTypeUpdate): void => {
    if (editingVoucherType) {
      updateVoucherTypeMutation.mutate({
        id: editingVoucherType.id,
        data: data as VoucherTypeUpdate,
      });
    } else {
      createVoucherTypeMutation.mutate(data as VoucherTypeCreate);
    }
  };

  if (voucherTypesError) {
    return (
      <div className="container mx-auto py-6">
        <div className="bg-destructive/10 p-4 rounded-md flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>Failed to load voucher types. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Voucher Types</h1>
        <Button
          onClick={() => {
            setEditingVoucherType(null);
            setFormOpen(true);
          }}
          className="flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Voucher Type
        </Button>
      </div>

      <div className="bg-white rounded-md shadow">
        <VoucherTypeDataTable 
          data={voucherTypesData}
          columns={columnsVoucherType}
          isLoading={isLoadingVoucherTypes}
          pagination={pagination}
          sorting={sorting}
          columnFilters={filters}
          searchValue={search}
          onSearchChange={setSearch}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
        />
      </div>

      {/* Voucher Type Form Dialog */}
      <VoucherTypeForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleVoucherTypeSubmit}
        defaultValues={editingVoucherType || undefined}
        isLoading={createVoucherTypeMutation.isPending || updateVoucherTypeMutation.isPending}
        mode={editingVoucherType ? 'edit' : 'create'}
      />

      {/* Delete Voucher Type Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voucher Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the voucher type &quot;{deletingVoucherType?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVoucherTypeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteVoucherTypeMutation.isPending}
              onClick={() => {
                if (deletingVoucherType) {
                  deleteVoucherTypeMutation.mutate(deletingVoucherType.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteVoucherTypeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VoucherTypesPage; 